import React, { useState, useEffect } from 'react';
import { DonationCampaign, Gelanggang, GelanggangDetail } from '../../types';
import { saveDonationCampaignDB, deleteDonationCampaignDB, getGelanggangDB } from '../../services/db';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import {
  Sparkles,
  ArrowLeft,
  Trash2,
  Save,
  Plus,
  X,
  Building,
  Target,
  FileText,
  Calendar,
  Layers,
  CheckCircle2,
  ShieldCheck
} from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

interface CampaignEditorPageProps {
  campaign?: DonationCampaign | null;
  onBack: () => void;
  onSaved: (campaigns: DonationCampaign[]) => void;
}

const slugify = (text: string, enforcePrefix = false): string => {
  let cleaned = text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!cleaned) return enforcePrefix ? 'tabung-infaq' : '';

  if (enforcePrefix && !cleaned.startsWith('tabung-') && cleaned !== 'tabung') {
    cleaned = `tabung-${cleaned}`;
  }
  return cleaned;
};

export const CampaignEditorPage: React.FC<CampaignEditorPageProps> = ({
  campaign,
  onBack,
  onSaved
}) => {
  const { showToast } = useToast();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState<DonationCampaign['category']>('Pembangunan Gelanggang');
  const [targetAmount, setTargetAmount] = useState<number>(10000);
  const [beneficiaryGelanggang, setBeneficiaryGelanggang] = useState<Gelanggang>('Semua Gelanggang');
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [story, setStory] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<DonationCampaign['status']>('Aktif');
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  const [milestones, setMilestones] = useState<{ amount: number; description: string }[]>([
    { amount: 50, description: '1 Unit Peralatan Asas Latihan' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getGelanggangDB().then(list => {
      if (list && list.length > 0) {
        setGelanggangList(list);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (campaign) {
      setTitle(campaign.title);
      setSlug(campaign.id);
      setCategory(campaign.category);
      setTargetAmount(campaign.targetAmount);
      setBeneficiaryGelanggang(campaign.beneficiaryGelanggang);
      setStory(campaign.story);
      setStartDate(campaign.startDate);
      setEndDate(campaign.endDate || '');
      setStatus(campaign.status);
      setIsFeatured(Boolean(campaign.isFeatured));
      setMilestones(
        campaign.impactMilestones && campaign.impactMilestones.length > 0
          ? campaign.impactMilestones
          : [{ amount: 50, description: '1 Unit Peralatan Asas Latihan' }]
      );
    }
  }, [campaign]);

  const handleTitleChange = (val: string) => {
    setTitle(val);
    // If new campaign and slug was empty or matching previous slugified title, auto update slug
    if (!campaign && (!slug || slug === slugify(title, true))) {
      setSlug(slugify(val, true));
    }
  };

  const handleAddMilestone = () => {
    setMilestones([...milestones, { amount: 100, description: '' }]);
  };

  const handleRemoveMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const handleMilestoneChange = (index: number, field: 'amount' | 'description', value: any) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    setMilestones(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      showToast('warning', 'Sila masukkan tajuk kempen.');
      return;
    }
    const cleanSlug = slugify(slug || title, true);
    if (!cleanSlug) {
      showToast('warning', 'Sila masukkan slug URL yang sah.');
      return;
    }
    if (targetAmount <= 0) {
      showToast('warning', 'Sasaran jumlah mestilah lebih daripada RM0.');
      return;
    }

    setIsSubmitting(true);
    try {
      const campToSave: DonationCampaign = {
        id: cleanSlug,
        title: title.trim(),
        category,
        targetAmount: Number(targetAmount),
        currentAmount: campaign?.currentAmount || 0,
        donorCount: campaign?.donorCount || 0,
        coverImage: campaign?.coverImage || undefined,
        story: story.trim(),
        organizerName: 'Persatuan Seni Silat Awang Sulung Merah Muda',
        beneficiaryGelanggang,
        startDate,
        endDate: endDate ? endDate : undefined,
        status,
        isFeatured,
        impactMilestones: milestones.filter(m => m.description.trim().length > 0)
      };

      const updatedList = await saveDonationCampaignDB(campToSave, campaign?.id);
      onSaved(updatedList);
      setIsSubmitting(false);
      showToast('success', campaign ? 'Kempen berjaya dikemaskini!' : 'Kempen infaq baru berjaya diterbitkan!');
      onBack();
    } catch {
      setIsSubmitting(false);
      showToast('error', 'Ralat semasa menyimpan kempen.');
    }
  };

  const handleDelete = async () => {
    if (!campaign) return;
    if (window.confirm(`Adakah anda pasti untuk memadam kempen "${campaign.title}"?`)) {
      const updated = await deleteDonationCampaignDB(campaign.id);
      onSaved(updated);
      showToast('success', 'Kempen telah dipadam.');
      onBack();
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[85vh]">
      {/* Top Header */}
      <PageHeader
        badgeText="Pihak Pengurusan"
        badgeIcon={ShieldCheck}
        title={campaign ? 'Kemaskini Kempen Infaq' : 'Cipta Kempen Infaq Baru'}
      >
        <button
          type="button"
          onClick={onBack}
          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Kembali</span>
        </button>
      </PageHeader>

      {/* Main Editor Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6 text-xs">
        {/* Row 1: Title & Slug & Category */}
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="font-extrabold text-slate-900 block">Tajuk Kempen Infaq *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Contoh: Tabung Karpet Keselamatan & Peralatan Latihan"
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-bold text-slate-900 outline-hidden"
              required
            />
          </div>

          {/* Slug URL Input */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="font-extrabold text-slate-900 flex items-center gap-1.5">
                <span>Slug URL Kempen *</span>
                <span className="text-[10px] font-normal text-slate-500">(Pautan Unik Web)</span>
              </label>
              <button
                type="button"
                onClick={() => setSlug(slugify(title, true))}
                className="text-[11px] font-bold text-asmm-red hover:underline flex items-center gap-1 cursor-pointer"
                title="Jana slug automatik bermula tabung- daripada tajuk"
              >
                <Sparkles className="w-3 h-3" />
                <span>Jana dari Tajuk</span>
              </button>
            </div>

            <div className="flex items-center rounded-xl border border-slate-200 focus-within:border-asmm-red bg-slate-50 overflow-hidden text-xs">
              <span className="pl-3.5 pr-1 font-mono text-slate-400 select-none shrink-0 font-medium">
                /donations/
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                placeholder="tabung-karpet-lunas"
                className="w-full py-2.5 pr-3.5 bg-transparent font-mono font-bold text-slate-900 outline-hidden"
                required
              />
            </div>
            <p className="text-[11px] text-slate-500">
              Pautan terus URL kempen: <code className="font-mono text-asmm-red font-bold">/donations/{slug || 'tabung-nama-kempen'}</code>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="font-bold text-slate-700 block">Kategori Tabung</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl font-medium outline-hidden"
              >
                <option value="Pembangunan Gelanggang">Pembangunan Gelanggang</option>
                <option value="Kebajikan & Asnaf">Kebajikan & Asnaf</option>
                <option value="Peralatan & Busana">Peralatan & Busana</option>
                <option value="Kontinjen & Sukan">Kontinjen & Sukan</option>
                <option value="Wakaf Am">Wakaf Am</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700 block">Gelanggang Penerima</label>
              <select
                value={beneficiaryGelanggang}
                onChange={(e) => setBeneficiaryGelanggang(e.target.value as any)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl font-medium outline-hidden"
              >
                <option value="Semua Gelanggang">Semua Gelanggang</option>
                {gelanggangList
                  .filter(g => g.name !== 'Semua Gelanggang' && g.name !== 'Tiada')
                  .map(g => (
                    <option key={g.id || g.name} value={g.name}>
                      {g.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700 block">Sasaran Dana (RM) *</label>
              <input
                type="number"
                min="100"
                step="50"
                value={targetAmount}
                onChange={(e) => setTargetAmount(Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-bold text-slate-900 outline-hidden"
                required
              />
            </div>
          </div>
        </div>

        {/* Row 2: Narrative Story */}
        <div className="space-y-1">
          <label className="font-extrabold text-slate-900 block">Keterangan & Kisah Kempen</label>
          <textarea
            rows={5}
            value={story}
            onChange={(e) => setStory(e.target.value)}
            placeholder="Terangkan secara jelas tujuan kutipan infaq, latar belakang keperluan gelanggang, dan impak positif kepada pesilat muda..."
            className="w-full px-3.5 py-2.5 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-normal leading-relaxed outline-hidden"
          />
        </div>

        {/* Row 3: Dates, Status, & Featured */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
          <div className="space-y-1">
            <label className="font-bold text-slate-700 block">Tarikh Mula</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl outline-hidden"
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-slate-700 block">Tarikh Tamat (Pilihan)</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl outline-hidden"
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-slate-700 block">Status Kempen</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-bold outline-hidden"
            >
              <option value="Aktif">Aktif (Kutipan Dibuka)</option>
              <option value="Draf">Draf (Tersorok)</option>
              <option value="Selesai">Selesai (Tamat)</option>
            </select>
          </div>
        </div>

        {/* Featured Toggle */}
        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="font-extrabold text-slate-900 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>Jadikan Kempen Pilihan Utama (Featured)</span>
            </div>
            <div className="text-[11px] text-slate-500">
              Kempen pilihan utama akan dipaparkan di bahagian atas halaman utama portal.
            </div>
          </div>
          <input
            type="checkbox"
            checked={isFeatured}
            onChange={(e) => setIsFeatured(e.target.checked)}
            className="w-4 h-4 text-asmm-red rounded border-slate-300 cursor-pointer"
          />
        </div>

        {/* Impact Milestones */}
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-extrabold text-slate-900">Pecahan Impak Dana (Milestones)</div>
              <div className="text-[11px] text-slate-500">Bantu penderma memahami nilai setiap ringgit yang disumbang</div>
            </div>
            <button
              type="button"
              onClick={handleAddMilestone}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl flex items-center gap-1 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Pecahan</span>
            </button>
          </div>

          <div className="space-y-2">
            {milestones.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-32 relative shrink-0">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 font-bold text-slate-400">RM</span>
                  <input
                    type="number"
                    value={m.amount}
                    onChange={(e) => handleMilestoneChange(idx, 'amount', Number(e.target.value))}
                    className="w-full pl-9 pr-2 py-2 bg-white border border-slate-200 rounded-xl font-bold outline-hidden"
                  />
                </div>
                <input
                  type="text"
                  value={m.description}
                  onChange={(e) => handleMilestoneChange(idx, 'description', e.target.value)}
                  placeholder="Penerangan impak (contoh: 1 Bilah Keris Warisan Latihan)"
                  className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl outline-hidden"
                />
                {milestones.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMilestone(idx)}
                    className="p-2 text-slate-400 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
          {campaign ? (
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
              <span>Padam Kempen</span>
            </button>
          ) : <div />}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSubmitting ? 'Menyimpan...' : 'Simpan & Terbitkan Kempen'}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
